import { SimpleButton, SimpleButtonProps } from '@schooly/style';
import { FC, useRef, useState } from 'react';

import { convertFileToBase64 } from '../../../../utils/convertFileToBase64';
import { SchoolTermsAndConditionsForm, UploadStatus } from './SchoolTermsAndConditionsContent';

const ALLOWED_FILE_TYPES = ['application/pdf'];
export const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type AddDocumentButtonProps = {
  setUploadStatus: (status: UploadStatus) => void;
  setDocument: (document: SchoolTermsAndConditionsForm['document']) => void;
} & SimpleButtonProps;

export const AddDocumentButton: FC<AddDocumentButtonProps> = ({
  setUploadStatus,
  setDocument,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isConverting, setIsConverting] = useState(false);

  const handleClick = () => {
    if (!inputRef.current) return;
    inputRef.current.click();
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget) {
      const { files } = e.currentTarget;

      if (files && files[0]) {
        const firstFile = files[0];
        const { type, size } = firstFile;

        if (!ALLOWED_FILE_TYPES.includes(type)) {
          setUploadStatus('formatError');
          return;
        }

        if (size > MAX_FILE_SIZE_BYTES) {
          setUploadStatus('sizeError');
          return;
        }

        try {
          setIsConverting(true);
          const base64Data = await convertFileToBase64(firstFile);

          setDocument({
            name: firstFile.name,
            data: base64Data,
          });
        } catch (_error) {
          setUploadStatus('uploadError');
        } finally {
          setIsConverting(false);
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }
      }
    }
  };

  return (
    <>
      <SimpleButton onClick={handleClick} disabled={isConverting} {...props} />
      <input
        ref={inputRef}
        accept={ALLOWED_FILE_TYPES.join(',')}
        type="file"
        onChange={onChange}
        hidden
      />
    </>
  );
};
